import Vue from 'vue';
import Vuex, {ActionContext} from 'vuex';
import {AuthStore} from '@/modules/Auth/stores/auth.store';
import {ProfileStore} from '@/modules/Profile/stores/profile.store';
import {ErrorStore} from '@/modules/Popup/stores/error.store';
import {ConfirmPopupStore} from '@/modules/Popup/stores/confirm.store';
import {DialogStore} from '@/modules/Popup/stores/dialog.store';
import {BookStore} from './modules/book.store';
import carList from '@/modules/CarList/stores/car-list.store';
import { IRequestAuth } from '@/modules/Auth/interfaces/request-auth.interface';
import router from '@/router';
import {IApp} from "@/interfaces/app.interface";
import ROUTE_NAME = IApp.ROUTE_NAME;
import {IAppState} from "@/interfaces/app-state.interface";
import {MAIN_ERRORS} from "@/interfaces/error-codes";
import {DashboardStore} from "@/modules/Dashboard/stores/dashboard.store";
import {ReportStore} from "@/modules/Report/stores/report.store";
import {ParksConfigStore} from "@/modules/ParksConfig/stores/parksConfig.store";
import {DeviceStatsStore} from "@/modules/DeviceStats/stores/device-stats.store";
import {CarStore} from "@/modules/Car/stores/car.store";
import {CorpsStore} from "@/modules/Corps/stores/corps.store";

type StoreActionContext = ActionContext<IAppState, IAppState>;

Vue.use(Vuex);

export interface OpenPageParams {
  routeName: ROUTE_NAME;
  query?: any;
  url?: string;
  params?: any;
  hash?: string;
}

export default new Vuex.Store({
  modules: {
    auth: AuthStore,
    profile: ProfileStore,
    error: ErrorStore,
    confirm: ConfirmPopupStore,
    dialog: DialogStore,
    book: BookStore,

    car: CarStore,
    carList,
    dashboard: DashboardStore,
    report: ReportStore,
    parksConfig: ParksConfigStore,
    deviceStats: DeviceStatsStore,
    corps: CorpsStore,
  },
  state: {
    isInitialized: false,
    isError: false,
    isLoadingApp: false,
    _loadingAppIntervalId: -1,
    loadingResponse: false,
  },
  mutations: {
    initialized(state) { state.isInitialized = true; },
    error(state) { state.isError = true; },
    startLoading(state) { state.loadingResponse = true },
    stopLoading(state) { state.loadingResponse = false },

    loadingApp(state, isLoading: boolean) {
      if (isLoading && !state._loadingAppIntervalId) {
        // Start the global loading if the request lasts more than 2 second.
        let timer = 0;
        state._loadingAppIntervalId = setInterval(() => {
          if (timer < 2000) {
            timer += 100;
          } else {
            state.isLoadingApp = true;
          }
        }, 100);
      } else {
        if (state._loadingAppIntervalId) {
          clearInterval(state._loadingAppIntervalId);
        }
        state._loadingAppIntervalId = null;
        state.isLoadingApp = false;
      }
    }
  },
  actions: {
    init({commit, dispatch}: StoreActionContext) {
      Promise.resolve()
        .then(() => dispatch('auth/init'))
        .then(() => dispatch('book/init'))
        .then(() => commit('initialized'))
        .catch(error => {
          console.error('init error', error);
          return dispatch('_clear', error);
        });
    },

    initialized({commit}: StoreActionContext) {
      commit('initialized');
    },

    error({commit, dispatch}: StoreActionContext, textId: string, responseData?: any, requestData?: any) {
      commit('error');
      return dispatch('error/open', {textId, responseData, requestData});
    },

    // TODO: all logic with open pages (without initializing page) and working with router must be here
    openPage({rootState, state, dispatch, commit}, payload: OpenPageParams) {
      if (!IApp.RedirectBlackList.includes(payload.routeName)) {
        localStorage.setItem(IApp.LocalStorageKeys.PreviousRoute, payload.routeName.toString());
      }
      const redirectToRoute = () => {
        const name = (payload.routeName as string);
        const { query, params, hash } = payload;

        switch (payload.routeName) {
          case ROUTE_NAME.REDIRECT:
            window.location.href = payload.url;
            break;

          case ROUTE_NAME.PAGE_AUTHORIZATION:
            window.location.href = IApp.RoutePath[ROUTE_NAME.PAGE_AUTHORIZATION];
            break;

          case ROUTE_NAME.PAGE_CONFIRM_EMAIL:
          case ROUTE_NAME.PAGE_PROFILE:
            router.push({name, query: query ? query : rootState.route.query, params, hash });
            break;

          case ROUTE_NAME.REDIRECT_WITHOUT_CACHE:
            document.location.reload();
            break;

          default:
            router.push({ name, query, params, hash });
            break;
        }
      };

      const initOpenPage = async () => {
        await redirectToRoute();
      };

      return initOpenPage();
    },

    _clear({dispatch, state}, error) {
      if (IRequestAuth.isAuthError(error) && !state.auth.isNewSession) {

        return Promise.resolve()
          .then(() => dispatch('auth/resolveError', error))
          .then(() => dispatch('init'));
      } else {

        return Promise.resolve()
          .then(() => dispatch('auth/clear'))
          .then(() => dispatch('order/clearOrderData')) // TODO: move to order loading
          .then((error) => {
            let params = {
              textId: 'error.openLater',
              responseData: error.response.data,
              requestData: error.response.config.data,
            };
            return dispatch('error', params);
          });
      }
    },

    async defaultResourceAction({ commit, dispatch }, payload: { asyncAction: Function, errorDispatch?: string, customErrorAction?: boolean, errorAction?: Function }) {
      commit('startLoading');
      let success = false;
      let error = null;
      let response = null;
      try {
        response = await payload.asyncAction();
        success = true;
      }
      catch (e) {
        error = e;
        if (payload.errorAction) await payload.errorAction(error);
        else if (payload.errorDispatch) await dispatch(payload.errorDispatch, error);
        else if (payload.customErrorAction) {}
        else await dispatch('handleError', error);
      }
      commit('stopLoading');
      return { success, error, response };
    },

    async handleError({ dispatch }, error) {
      console.error(error);

      const errorTextId = {
        [MAIN_ERRORS.DEFAULT_RELOAD]: 'error.customTextAndReload',
        [MAIN_ERRORS.DEFAULT]: 'error.customText',
      };
      let textId = errorTextId[error.status] ? errorTextId[error.status] : 'error.customText';
      const params = {
        textId,
        payload: error.errors && error.errors.length
          ? error.errors.join(', ')
          : error.response && error.response.data && error.response.data.error
            ? error.response.data.error
            : error,
      };
      await dispatch('error/open', params);
    },
  },
} as any);
